import { onlineManager, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { canvasserApi } from '../lib/api'
import { isNetworkError } from '../lib/syncEngine'
import { haversineMeters } from '../lib/walkGeo'
import { clearWalkOrder, readWalkOrder, withoutNextLeg, writeWalkOrder } from '../lib/walkOrderStore'
import { approximateOrder, type WalkStop } from '../lib/walkStops'
import type { ComputeRouteResponse } from '../types/canvasser'
import type { GeoPosition } from './useGeolocation'
import { deriveRouteState, deriveRouteUiState } from './routeUiState'

/** Movement before the next leg is re-requested. Driving covers ground fast; 50 m on a car meant a
 *  request every ~4 s (map audit 2026-10-09). */
const MOVE_THRESHOLD_M = { walking: 50, driving: 250 } as const
/** One under the server's trip cap (CanvassingService.MaxTripWaypoints = 100 incl. the GPS point). */
const MAX_TRIP_STOPS = 99
/** [A3 §5] Consecutive unavailable route signals (unrouted response / network error) before the
 *  phone orders the walk itself. One transient failure or a brief circuit-open never freezes. */
const FALLBACK_AFTER_SIGNALS = 2
/** [A3 §5] How long the fallback waits for a first GPS fix before starting from list order. */
const GPS_WAIT_MS = 10_000

type FrozenKind = 'optimized' | 'approximate'
/** Per-scope frozen state. `route` = the persisted road line (served only when there is no live data). */
interface Slot { scope: string; frozen: { kind: FrozenKind; order: string[] } | null; route: ComputeRouteResponse | null }
/** Per-scope unavailable-signal tally. `last` is reset on an approximate freeze, so `routingBack`
 *  only reflects responses that settled after it. */
interface Signals { scope: string; count: number; last: 'available' | 'unavailable' | null }

function loadSlot(turfId: string, mode: 'walking' | 'driving'): Slot {
  const stored = readWalkOrder(turfId, mode)
  return {
    scope: `${turfId}|${mode}`,
    frozen: stored ? { kind: stored.kind, order: stored.order } : null,
    route: stored?.route ?? null,
  }
}

/** Every key of `sub` is in `order`, in the same relative order (a route through the frozen order). */
function followsOrder(sub: readonly string[], order: readonly string[]): boolean {
  const rank = new Map(order.map((k, i) => [k, i]))
  let prev = -1
  for (const k of sub) {
    const r = rank.get(k)
    if (r == null || r < prev) return false
    prev = r
  }
  return true
}

// [A3 final review I-1] TanStack's onlineManager starts `true` and only flips on window online/offline
// EVENTS — it never reads navigator.onLine at boot, so a phone opened with no signal looked online.
// Read both (locally; onlineManager itself is left alone) and listen to both.
const subscribeOnline = (cb: () => void) => {
  const unsub = onlineManager.subscribe(cb)
  window.addEventListener('online', cb)
  window.addEventListener('offline', cb)
  return () => {
    unsub()
    window.removeEventListener('online', cb)
    window.removeEventListener('offline', cb)
  }
}
const getOnline = () => onlineManager.isOnline() && navigator.onLine !== false

/** Why ⚡ cannot optimize right now. */
export type OptimizeBlockedReason = 'tooFew' | 'noGps' | 'tooMany'

interface UseWalkRouteParams {
  /** OPEN stops to route through, in base walk order. Done stops are not passed. */
  stops: WalkStop[]
  turfId: string
  mode: 'walking' | 'driving'
  currentPosition: GeoPosition | null
  enabled?: boolean
  /** [O1 P4-S8] The organizer hand-made this walk order. Locks it: never optimize, never freeze.
   *  Absent on an old offline cache — behaves exactly as before (auto-optimize allowed). */
  orderIsManual?: boolean
}

/**
 * Route through the turf's open stops — ONE waypoint per address (stop key as the id).
 * Once the trip solver returns an optimized order it is FROZEN for this turf + mode, so pin numbers
 * don't reshuffle mid-walk; later requests send that order with optimize=false and only refresh the
 * geometry and next leg. `reoptimize()` (the ⚡ button) drops the frozen order.
 *
 * [A3 §5] The frozen order (+ last road line) persists per turf + mode in sessionStorage, so a reload
 * never reshuffles and an offline cold start still has its order and line. When routing is unavailable
 * (offline, or 2 consecutive unrouted/network-failed responses) and nothing is frozen, the phone orders
 * the walk itself (`approximateOrder`: nearest the GPS fix, waiting up to 10 s for one) and freezes that,
 * kind 'approximate', never "optimized". Nothing re-orders a frozen order except ⚡.
 */
export function useWalkRoute({ stops, turfId, mode, currentPosition, enabled = true, orderIsManual }: UseWalkRouteParams) {
  const locked = orderIsManual === true
  const scope = `${turfId}|${mode}`
  const online = useSyncExternalStore(subscribeOnline, getOnline, getOnline)
  // Frozen order for THIS scope, restored from sessionStorage on mount / scope change (online too).
  const [slotState, setSlot] = useState<Slot>(() => loadSlot(turfId, mode))
  let slot = slotState
  if (slot.scope !== scope) {
    slot = loadSlot(turfId, mode)
    setSlot(slot)
  }
  // A frozen order captured before the lock flag arrived must not keep applying.
  const frozen = locked ? null : slot.frozen
  const frozenOrder = frozen?.order ?? null
  const frozenKind = frozen?.kind ?? null

  const [signalState, setSignals] = useState<Signals>({ scope, count: 0, last: null })
  const signals: Signals = signalState.scope === scope ? signalState : { scope, count: 0, last: null }

  const waypoints = useMemo(() => {
    const located = stops.filter(s => s.lat != null && s.lng != null)
    let ordered = located
    if (frozenOrder) {
      const rank = new Map(frozenOrder.map((k, i) => [k, i]))
      const rankOf = (key: string) => rank.get(key) ?? Number.MAX_SAFE_INTEGER
      ordered = [...located].sort((a, b) => rankOf(a.key) - rankOf(b.key))
    }
    return ordered.map(s => ({ contactId: s.key, latitude: s.lat!, longitude: s.lng! }))
  }, [stops, frozenOrder])

  const lastFetchRef = useRef<{ lat: number; lng: number } | null>(null)
  const stablePosition = useMemo(() => {
    if (!currentPosition) return null
    const last = lastFetchRef.current
    if (last && haversineMeters(last.lat, last.lng, currentPosition.lat, currentPosition.lng) < MOVE_THRESHOLD_M[mode])
      return last
    const rounded = {
      lat: Math.round(currentPosition.lat * 10000) / 10000,
      lng: Math.round(currentPosition.lng * 10000) / 10000,
    }
    lastFetchRef.current = rounded
    return rounded
  }, [currentPosition, mode])

  // [I-2] Auto-optimize is decided ONCE per turf + mode, on the first route request that has a GPS
  // position: optimizable (≤ cap) → optimize (and freeze below); over the cap → off for this scope for
  // good, so a big turf that later drops under the cap never reshuffles mid-walk. After that only ⚡
  // (`reoptimize`) re-solves. Set during render (guarded) so the very first request already carries it.
  const [autoOpt, setAutoOpt] = useState<{ scope: string; on: boolean } | null>(null)
  let autoOn = autoOpt?.scope === scope ? autoOpt.on : null
  if (!locked && autoOn == null && stablePosition && enabled && waypoints.length >= 2) {
    autoOn = waypoints.length <= MAX_TRIP_STOPS
    setAutoOpt({ scope, on: autoOn })
  }
  /** Bumped by ⚡ / retry: a fresh key, so the request really goes out again (I-3). */
  const [nonce, setNonce] = useState(0)

  const optimize = !locked && !!stablePosition && !frozenOrder && autoOn === true && waypoints.length <= MAX_TRIP_STOPS
  const signature = waypoints.map(w => w.contactId).join(',')
  const positionKey = stablePosition ? `${stablePosition.lat},${stablePosition.lng}` : 'none'
  const optimizeBlockedReason: OptimizeBlockedReason | null =
    waypoints.length < 2 ? 'tooFew'
      : !currentPosition ? 'noGps'
        : waypoints.length > MAX_TRIP_STOPS ? 'tooMany'
          : null

  const queryEnabled = enabled && waypoints.length >= 2
  const query = useQuery({
    queryKey: ['canvasser', 'route', turfId, mode, optimize, signature, positionKey, nonce],
    queryFn: async (): Promise<ComputeRouteResponse> => (await canvasserApi.computeRoute({
      waypoints,
      mode,
      optimize,
      currentPosition: stablePosition ? { latitude: stablePosition.lat, longitude: stablePosition.lng } : null,
    })).data,
    enabled: queryEnabled,
    // Keep the previous route on screen while the next one loads (every visit / 50 m changes the key),
    // but ONLY within the same turf + mode — never carry another turf's line across a switch.
    placeholderData: (prev: ComputeRouteResponse | undefined, prevQuery?: { queryKey: readonly unknown[] }) =>
      prevQuery?.queryKey[2] === turfId && prevQuery?.queryKey[3] === mode ? prev : undefined,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: 1,
  })

  const qc = useQueryClient()
  const data = query.data ?? null
  const isPlaceholder = query.isPlaceholderData
  useEffect(() => {
    // Never freeze from placeholder data: after ⚡ the placeholder is the OLD optimized response, and
    // freezing it would silently undo the reoptimize.
    if (locked || frozenOrder || isPlaceholder || !data?.optimized || data.routingAvailable === false) return
    // Freezing changes the key (optimize=false, signature = the optimized order). Seed that key with
    // the response we already have, so freezing never fires a redundant second request.
    qc.setQueryData(['canvasser', 'route', turfId, mode, false, data.waypointOrder.join(','), positionKey, nonce], data)
    setSlot({ scope, frozen: { kind: 'optimized', order: data.waypointOrder }, route: withoutNextLeg(data) })
    writeWalkOrder(turfId, mode, { kind: 'optimized', order: data.waypointOrder, route: data })
  }, [data, isPlaceholder, locked, frozenOrder, scope, qc, turfId, mode, positionKey, nonce])

  // [A3 §5] Persist the latest real road line through the frozen order, so a reload (or an offline
  // cold start) still draws it. An unrouted response never overwrites a good line.
  useEffect(() => {
    if (!frozen || isPlaceholder || !data || data.routingAvailable === false) return
    if (!followsOrder(data.waypointOrder, frozen.order)) return
    writeWalkOrder(turfId, mode, { kind: frozen.kind, order: frozen.order, route: data })
  }, [data, isPlaceholder, frozen, turfId, mode])

  // Latest stops/position for the GPS-wait timer and ⚡ (read after commit, so callbacks stay stable).
  const stopsRef = useRef(stops)
  const positionRef = useRef(currentPosition)
  useEffect(() => { stopsRef.current = stops; positionRef.current = currentPosition })

  const freezeApproximate = useCallback((from: { lat: number; lng: number } | null) => {
    const order = approximateOrder(stopsRef.current, from ? { lat: from.lat, lng: from.lng } : null)
    setSlot({ scope, frozen: { kind: 'approximate', order }, route: null })
    setSignals(s => (s.scope === scope ? { ...s, last: null } : s))
    writeWalkOrder(turfId, mode, { kind: 'approximate', order, route: null })
  }, [scope, turfId, mode])

  /** [A3 final review I-2] Scope of a ⚡-initiated server attempt still waiting for its reply. */
  const replanPendingRef = useRef<string | null>(null)

  // [A3 §5] Unavailable signals: a settled, non-placeholder unrouted response, or a network error.
  // Each settle counts once (by its timestamp); any available response resets the run.
  const seenRef = useRef({ data: 0, error: 0 })
  const { dataUpdatedAt, errorUpdatedAt, error } = query
  useEffect(() => {
    if (isPlaceholder) return
    const seen = seenRef.current
    const newData = dataUpdatedAt > seen.data && data != null
    const newError = errorUpdatedAt > seen.error
    if (newData) seen.data = dataUpdatedAt
    if (newError) seen.error = errorUpdatedAt
    let signal: 'available' | 'unavailable' | null = null
    if (newError && (!newData || errorUpdatedAt >= dataUpdatedAt)) {
      if (isNetworkError(error)) signal = 'unavailable'
    } else if (newData) {
      signal = data.routingAvailable === false ? 'unavailable' : 'available'
    }
    // [I-2] The first settle after a ⚡ server attempt IS its reply. Unavailable: the ⚡ tap owns the
    // change, so order the walk on the phone now — otherwise pins sit in list order and a LATER
    // unavailable reply would freeze a second reshuffle nobody tapped. Any settle ends the attempt.
    const replanReply = (newData || newError) && replanPendingRef.current === scope
    if (newData || newError) replanPendingRef.current = null
    if (!signal) return
    setSignals(s => {
      const base = s.scope === scope ? s : { scope, count: 0, last: null }
      return { scope, count: signal === 'available' ? 0 : base.count + 1, last: signal }
    })
    // Never over an order that froze meanwhile (e.g. went offline while the reply was in flight).
    if (replanReply && signal === 'unavailable' && !locked && !frozenOrder) freezeApproximate(positionRef.current)
  }, [dataUpdatedAt, errorUpdatedAt, error, data, isPlaceholder, scope, locked, frozenOrder, freezeApproximate])

  // [A3 §5] Fallback trigger: not locked, nothing frozen, something to order, and routing unavailable
  // (offline: at once; else after 2 consecutive signals). No GPS fix: wait up to 10 s ("locating"); a
  // fix arriving sooner freezes at once. It never fires once frozen, so it can never re-order.
  const fallbackDue = !locked && !frozen && enabled && waypoints.length >= 2
    && (!online || signals.count >= FALLBACK_AFTER_SIGNALS)
  const hasPosition = currentPosition != null
  useEffect(() => {
    if (!fallbackDue) return
    if (hasPosition) { freezeApproximate(positionRef.current); return }
    const t = setTimeout(() => freezeApproximate(null), GPS_WAIT_MS)
    return () => clearTimeout(t)
  }, [fallbackDue, hasPosition, freezeApproximate])
  const awaitingGps = fallbackDue && !hasPosition

  /** ⚡ / "Routing unavailable — tap to retry": drop the frozen order, turn optimizing back on for
   *  this scope, and bump the key so a request REALLY goes out (a cached or unchanged key did nothing). */
  const reoptimize = useCallback(() => {
    // Locked (hand-made order): never optimize — still bump the nonce so the routing-unavailable
    // retry re-requests the route in walk order.
    if (!locked) {
      clearWalkOrder(turfId, mode)
      // [A3 §5] Routing still unavailable (offline, or the fallback condition holds): re-run the phone's
      // own order from where the canvasser is now, with no 2-signal or GPS wait. Otherwise ask the server.
      if (!online || signals.count >= FALLBACK_AFTER_SIGNALS) {
        freezeApproximate(positionRef.current)
      } else {
        setSlot({ scope, frozen: null, route: null })
        setAutoOpt({ scope, on: true })
        // [I-2] Its reply decides: unavailable → the phone's own order at once (see the signal effect).
        if (queryEnabled) replanPendingRef.current = scope
      }
    }
    setNonce(n => n + 1)
  }, [scope, locked, turfId, mode, online, signals.count, freezeApproximate, queryEnabled])

  // No live data for this scope (offline cold start): serve the persisted road line.
  const routeData = data ?? (frozen ? slot.route : null)
  const { routingUnavailable, isRouteOptimized } = deriveRouteUiState(routeData, frozenKind === 'optimized')
  const routeState = deriveRouteState({ locked, frozenKind, routeData, online, awaitingGps, isPlaceholder })
  /** Routing answered again while on the phone's own order: the "Routing's back" nudge (§6). */
  const routingBack = frozenKind === 'approximate' && online && signals.last === 'available'

  return {
    routeData,
    /** Stop keys in route order. Once frozen it may still contain stops that are now done and omits
     *  un-geocoded/new stops — consumers must intersect it with their stop list (`orderStops` in
     *  lib/walkStops does this) rather than assume it matches. */
    order: frozenOrder ?? (data?.waypointOrder ?? null),
    routingUnavailable,
    isRouteOptimized,
    isFetching: query.isFetching,
    reoptimize,
    /** [O1 P4-S8] Hand-made order: ⚡ is hidden (except as the routing-unavailable retry). */
    orderLocked: locked,
    /** GPS present and 2..cap located open stops: a ⚡ solve could succeed. */
    canOptimize: optimizeBlockedReason == null,
    /** Why ⚡ can't optimize right now (drives its disabled label), or null. */
    optimizeBlockedReason,
    /** [A3 §5] What the map labels the order in effect (`ROUTE_STATE_LABEL`). */
    routeState,
    /** [A3 §5] On the approximate order and a route request just succeeded: invite ⚡ (§6). */
    routingBack,
  }
}
